import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  SettingOutlined as RouteOutlined,
  SwapOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Create axios instance with JWT authentication (for admin)
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const ProductRouting = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: null,
  });
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [productCodes, setProductCodes] = useState([]);

  // Modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // Load initial data
  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, filters.search, filters.is_active, sortField, sortOrder]);

  // Load product codes once on mount
  useEffect(() => {
    fetchProductCodes();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search,
        is_active: filters.is_active,
        sort_by: sortField,
        sort_order: sortOrder,
      };

      const response = await adminApi.get('/product-routing/', { params });

      setData(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        current: response.data.pagination.page,
      }));
    } catch (error) {
      console.error('Error fetching product routing:', error);
      message.error('Gagal memuat data product routing');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductCodes = async () => {
    try {
      const response = await adminApi.get('/product-routing/meta/product-codes');
      setProductCodes(response.data.data);
    } catch (error) {
      console.error('Error fetching product codes:', error);
    }
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });

    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, is_active: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setFilters({
      search: '',
      is_active: null,
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      source_product_code: record.source_product_code,
      target_product_code: record.target_product_code,
      priority: record.priority,
      is_active: record.is_active,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.delete(`/product-routing/${id}`);
      message.success('Product routing berhasil dihapus');
      fetchData();
    } catch (error) {
      console.error('Error deleting product routing:', error);
      message.error('Gagal menghapus product routing');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await adminApi.patch(`/product-routing/${id}/toggle-status`);
      message.success('Status berhasil diubah');
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      message.error('Gagal mengubah status');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingItem) {
        // Update
        await adminApi.put(`/product-routing/${editingItem.id}`, values);
        message.success('Product routing berhasil diperbarui');
      } else {
        // Create
        await adminApi.post('/product-routing/', values);
        message.success('Product routing berhasil ditambahkan');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('Error saving product routing:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Gagal menyimpan product routing');
      }
    }
  };

  const columns = [
    {
      title: 'Source Product',
      dataIndex: 'source_product_code',
      key: 'source_product_code',
      sorter: true,
      width: 200,
      render: (text, record) => (
        <div>
          <Tag color="blue" icon={<RouteOutlined />}>
            {text}
          </Tag>
          {record.source_product_name && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {record.source_product_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'arrow',
      width: 50,
      render: () => (
        <ArrowRightOutlined style={{ color: '#1890ff', fontSize: 16 }} />
      ),
    },
    {
      title: 'Target Product',
      dataIndex: 'target_product_code',
      key: 'target_product_code',
      sorter: true,
      width: 200,
      render: (text, record) => (
        <div>
          <Tag color="green" icon={<SwapOutlined />}>
            {text}
          </Tag>
          {record.target_product_name && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {record.target_product_name}
            </div>
          )}
          {record.target_supplier_code && (
            <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
              Supplier: {record.target_supplier_code}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      sorter: true,
      width: 100,
      render: (priority) => (
        <Tag color={priority === 1 ? 'green' : 'orange'}>
          {priority}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleStatus(record.id)}
          checkedChildren="Aktif"
          unCheckedChildren="Nonaktif"
        />
      ),
    },
    {
      title: 'Dibuat',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: 180,
      render: (date) => new Date(date).toLocaleString('id-ID'),
    },
    {
      title: 'Aksi',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Apakah Anda yakin ingin menghapus item ini?"
            onConfirm={() => handleDelete(record.id)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Tooltip title="Hapus">
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <RouteOutlined /> Product Routing
            </Title>
            <div style={{ color: '#666', marginTop: 4 }}>
              Atur routing produk dari source ke target
            </div>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchData}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Tambah Routing
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Cari kode produk..."
              allowClear
              enterButton
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Select
              placeholder="Filter Status"
              allowClear
              style={{ width: '100%' }}
              onChange={handleStatusFilter}
              value={filters.is_active}
            >
              <Option value={true}>Aktif</Option>
              <Option value={false}>Nonaktif</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6} md={4}>
            <Button onClick={handleReset}>
              Reset Filter
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} item`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      <Modal
        title={editingItem ? 'Edit Product Routing' : 'Tambah Product Routing'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            priority: 1,
            is_active: true,
          }}
        >
          <Form.Item
            name="source_product_code"
            label="Source Product Code"
            rules={[
              { required: true, message: 'Source product code harus diisi!' },
            ]}
            extra="Kode produk yang diminta oleh merchant"
          >
            <Select
              placeholder="Pilih source product code"
              showSearch
              allowClear
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              notFoundContent="Tidak ada kode produk"
            >
              {productCodes.map(item => (
                <Option key={item.product_code} value={item.product_code}>
                  {item.product_code} - {item.product_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="target_product_code"
            label="Target Product Code"
            rules={[
              { required: true, message: 'Target product code harus diisi!' },
            ]}
            extra="Kode produk yang akan digunakan untuk proses ke supplier"
          >
            <Select
              placeholder="Pilih target product code"
              showSearch
              allowClear
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              notFoundContent="Tidak ada kode produk"
            >
              {productCodes.map(item => (
                <Option key={item.product_code} value={item.product_code}>
                  {item.product_code} - {item.product_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Priority harus diisi!' }]}
            extra="Semakin kecil angka, semakin tinggi prioritas"
          >
            <InputNumber
              min={1}
              max={100}
              style={{ width: '100%' }}
              placeholder="Masukkan priority (1-100)"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                {editingItem ? 'Update' : 'Simpan'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductRouting;
